import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getApiUrl } from '../config';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export default function Settings() {
  const [formData, setFormData] = useState({
    logo: '',
    menu: [],
    mainButton: { text: '', link: '' },
    headerButtons: [],
  });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logoFile, setLogoFile] = useState(null);
  
  // Menu management states
  const [menuDialog, setMenuDialog] = useState(false);
  const [editMenuIndex, setEditMenuIndex] = useState(null);
  const [menuForm, setMenuForm] = useState({ title: '', link: '', submenu: [] });
  const [submenuDialog, setSubmenuDialog] = useState(false);
  const [editSubIndex, setEditSubIndex] = useState(null);
  const [submenuForm, setSubmenuForm] = useState({ title: '', link: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/settings'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          logo: data.logo || '',
          menu: data.menu || [],
          mainButton: data.mainButton || { text: '', link: '' },
          headerButtons: Array.isArray(data.headerButtons) ? data.headerButtons : [],
        });
        setMaintenanceMode(data.maintenanceMode || false);
      }
    } catch (error) {
      console.error('خطا در دریافت تنظیمات:', error);
      setMessage({ type: 'error', text: 'خطا در دریافت تنظیمات' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      } else {
        formDataToSend.append('logo', formData.logo);
      }
      formDataToSend.append('menu', JSON.stringify(formData.menu));
      formDataToSend.append('mainButton', JSON.stringify(formData.mainButton));
      formDataToSend.append('headerButtons', JSON.stringify(formData.headerButtons || []));

      const response = await fetch(getApiUrl('/api/settings'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        const result = await response.json();
        setFormData(prev => ({
          ...prev,
          logo: result.logo || prev.logo
        }));
        setLogoFile(null);
        setMessage({ type: 'success', text: 'تنظیمات با موفقیت ذخیره شد' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'خطا در ذخیره تنظیمات' });
      }
    } catch (error) {
      console.error('خطا در ذخیره تنظیمات:', error);
      setMessage({ type: 'error', text: 'خطا در ذخیره تنظیمات' });
    } finally {
      setLoading(false);
    }
  };

  const handleMaintenanceToggle = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const newMaintenanceMode = !maintenanceMode;
      
      const response = await fetch(getApiUrl('/api/settings/maintenance-mode'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ maintenanceMode: newMaintenanceMode })
      });

      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(newMaintenanceMode);
        setMessage({ 
          type: 'success', 
          text: data.message || `حالت تعمیر ${newMaintenanceMode ? 'فعال' : 'غیرفعال'} شد` 
        });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'خطا در تغییر حالت تعمیر' });
      }
    } catch (error) {
      console.error('خطا در تغییر حالت تعمیر:', error);
      setMessage({ type: 'error', text: 'خطا در تغییر حالت تعمیر' });
    } finally {
      setLoading(false);
    }
  };

  // Menu CRUD functions
  const handleMenuSave = () => {
    if (!menuForm.title.trim() || !menuForm.link.trim()) {
      setMessage({ type: 'error', text: 'عنوان و لینک منو الزامی است' });
      return;
    }
    let menu = [...formData.menu];
    if (editMenuIndex !== null) {
      menu[editMenuIndex] = { ...menuForm };
    } else {
      menu.push({ ...menuForm });
    }
    setFormData(prev => ({ ...prev, menu }));
    setMenuDialog(false);
    setMenuForm({ title: '', link: '', submenu: [] });
    setEditMenuIndex(null);
    setMessage({ type: 'success', text: 'منو با موفقیت ذخیره شد' });
  };

  const handleMenuEdit = (idx) => {
    setEditMenuIndex(idx);
    setMenuForm({ ...formData.menu[idx] });
    setMenuDialog(true);
  };

  const handleMenuDelete = (idx) => {
    let menu = [...formData.menu];
    menu.splice(idx, 1);
    setFormData(prev => ({ ...prev, menu }));
    setMessage({ type: 'success', text: 'منو با موفقیت حذف شد' });
  };

  // Submenu CRUD functions
  const handleSubSave = () => {
    if (!submenuForm.title.trim() || !submenuForm.link.trim()) {
      setMessage({ type: 'error', text: 'عنوان و لینک زیرمنو الزامی است' });
      return;
    }
    let submenu = menuForm.submenu ? [...menuForm.submenu] : [];
    if (editSubIndex !== null) {
      submenu[editSubIndex] = { ...submenuForm };
    } else {
      submenu.push({ ...submenuForm });
    }
    setMenuForm(prev => ({ ...prev, submenu }));
    setSubmenuDialog(false);
    setSubmenuForm({ title: '', link: '' });
    setEditSubIndex(null);
    setMessage({ type: 'success', text: 'زیرمنو با موفقیت ذخیره شد' });
  };

  const handleSubEdit = (idx) => {
    setEditSubIndex(idx);
    setSubmenuForm({ ...menuForm.submenu[idx] });
    setSubmenuDialog(true);
  };

  const handleSubDelete = (idx) => {
    let submenu = [...(menuForm.submenu || [])];
    submenu.splice(idx, 1);
    setMenuForm(prev => ({ ...prev, submenu }));
    setMessage({ type: 'success', text: 'زیرمنو با موفقیت حذف شد' });
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      setFormData(prev => ({ ...prev, logo: file.name }));
    }
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setFormData(prev => ({ ...prev, logo: '' }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Vazir', mb: 4 }}>
        تنظیمات سایت
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Maintenance Mode Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" sx={{ fontFamily: 'Vazir' }}>
                  حالت تعمیر
                </Typography>
              </Box>
              
              <Paper sx={{ p: 2, bgcolor: maintenanceMode ? 'warning.light' : 'success.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ mr: 1, color: maintenanceMode ? 'warning.dark' : 'success.dark' }} />
                    <Typography variant="body1" sx={{ fontFamily: 'Vazir' }}>
                      {maintenanceMode ? 'سایت در حالت تعمیر است' : 'سایت فعال است'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={maintenanceMode}
                        onChange={handleMaintenanceToggle}
                        disabled={loading}
                        color="warning"
                      />
                    }
                    label=""
                  />
                </Box>
                
                <Typography variant="body2" sx={{ fontFamily: 'Vazir', color: 'text.secondary' }}>
                  {maintenanceMode 
                    ? 'در حالت تعمیر، کاربران نمی‌توانند به سایت دسترسی داشته باشند'
                    : 'سایت برای کاربران قابل دسترسی است'
                  }
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* General Settings Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Vazir' }}>
                تنظیمات عمومی
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir', mb: 2 }}>
                    لوگو سایت
                  </Typography>
                  
                  {formData.logo && (
                    <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'Vazir', mb: 1 }}>
                        لوگوی فعلی: {formData.logo}
                      </Typography>
                      {formData.logo.startsWith('/file/') && (
                        <Box sx={{ mb: 2 }}>
                          <img 
                            src={getApiUrl(formData.logo)} 
                            alt="لوگوی فعلی" 
                            style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleLogoRemove}
                        sx={{ fontFamily: 'Vazir' }}
                      >
                        حذف لوگو
                      </Button>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      sx={{ fontFamily: 'Vazir' }}
                    >
                      انتخاب فایل لوگو
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                    {logoFile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir', color: 'success.main' }}>
                          فایل انتخاب شد: {logoFile.name}
                        </Typography>
                        <img 
                          src={URL.createObjectURL(logoFile)} 
                          alt="پیش‌نمایش لوگو" 
                          style={{ maxWidth: '150px', maxHeight: '75px', objectFit: 'contain' }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
                
                <TextField
                  fullWidth
                  label="متن دکمه اصلی"
                  value={formData.mainButton.text}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    mainButton: { ...formData.mainButton, text: e.target.value }
                  })}
                  size="medium"
                  sx={{ mb: 3 }}
                />
                
                <TextField
                  fullWidth
                  label="لینک دکمه اصلی"
                  value={formData.mainButton.link}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    mainButton: { ...formData.mainButton, link: e.target.value }
                  })}
                  size="medium"
                  sx={{ mb: 3 }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ fontFamily: 'Vazir' }}
                >
                  {loading ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Menu Management Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontFamily: 'Vazir' }}>
                  مدیریت منو
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setMenuDialog(true)}
                  sx={{ fontFamily: 'Vazir' }}
                >
                  افزودن منو
                </Button>
              </Box>
              
              <TableContainer component={Paper} sx={{ fontFamily: 'Vazir' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">عنوان</TableCell>
                      <TableCell align="right">لینک</TableCell>
                      <TableCell align="right">زیرمنوها</TableCell>
                      <TableCell align="center">عملیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.menu.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="right">{item.title}</TableCell>
                        <TableCell align="right">{item.link}</TableCell>
                        <TableCell align="right">
                          {item.submenu && item.submenu.length > 0 && (
                            <List dense>
                              {item.submenu.map((sub, sidx) => (
                                <ListItem key={sidx} sx={{ py: 0 }}>
                                  <ListItemText 
                                    primary={sub.title} 
                                    secondary={sub.link}
                                    sx={{ fontFamily: 'Vazir' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => handleMenuEdit(idx)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleMenuDelete(idx)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Menu Dialog */}
      <Dialog open={menuDialog} onClose={() => setMenuDialog(false)} dir="rtl" maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          {editMenuIndex !== null ? 'ویرایش منو' : 'افزودن منو'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="عنوان منو"
            fullWidth
            value={menuForm.title}
            onChange={(e) => setMenuForm(prev => ({ ...prev, title: e.target.value }))}
            sx={{ fontFamily: 'Vazir', mb: 2, mt: 1 }}
          />
          <TextField
            label="لینک منو"
            fullWidth
            value={menuForm.link}
            onChange={(e) => setMenuForm(prev => ({ ...prev, link: e.target.value }))}
            sx={{ fontFamily: 'Vazir', mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Vazir' }}>
              زیرمنوها
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setSubmenuDialog(true)}
              sx={{ fontFamily: 'Vazir' }}
            >
              افزودن زیرمنو
            </Button>
          </Box>
          
          {menuForm.submenu && menuForm.submenu.length > 0 && (
            <TableContainer component={Paper} sx={{ fontFamily: 'Vazir', mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right">عنوان</TableCell>
                    <TableCell align="right">لینک</TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menuForm.submenu.map((sub, idx) => (
                    <TableRow key={idx}>
                      <TableCell align="right">{sub.title}</TableCell>
                      <TableCell align="right">{sub.link}</TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" onClick={() => handleSubEdit(idx)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleSubDelete(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMenuDialog(false)} sx={{ fontFamily: 'Vazir' }}>
            انصراف
          </Button>
          <Button onClick={handleMenuSave} variant="contained" sx={{ fontFamily: 'Vazir' }}>
            {editMenuIndex !== null ? 'ذخیره' : 'افزودن'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submenu Dialog */}
      <Dialog open={submenuDialog} onClose={() => setSubmenuDialog(false)} dir="rtl">
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          {editSubIndex !== null ? 'ویرایش زیرمنو' : 'افزودن زیرمنو'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="عنوان زیرمنو"
            fullWidth
            value={submenuForm.title}
            onChange={(e) => setSubmenuForm(prev => ({ ...prev, title: e.target.value }))}
            sx={{ fontFamily: 'Vazir', mb: 2, mt: 1 }}
          />
          <TextField
            label="لینک زیرمنو"
            fullWidth
            value={submenuForm.link}
            onChange={(e) => setSubmenuForm(prev => ({ ...prev, link: e.target.value }))}
            sx={{ fontFamily: 'Vazir', mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmenuDialog(false)} sx={{ fontFamily: 'Vazir' }}>
            انصراف
          </Button>
          <Button onClick={handleSubSave} variant="contained" sx={{ fontFamily: 'Vazir' }}>
            {editSubIndex !== null ? 'ذخیره' : 'افزودن'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 